import { useEffect, useState, type FormEvent } from 'react';
import { getRepo, isDemoMode, type Settings } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import {
  Button, Card, CardBody, CardHeader, Field, Input, Spinner, Switch, Textarea, useToast,
} from '../../components/ui';

const URL_FIELDS: { key: keyof Settings['social']; label: string; placeholder: string }[] = [
  { key: 'facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/yourpage' },
  { key: 'twitter', label: 'Twitter / X URL', placeholder: 'https://x.com/yourhandle' },
  { key: 'instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/yourhandle' },
  { key: 'youtube', label: 'YouTube URL', placeholder: 'https://youtube.com/@yourchannel' },
];

const isHttp = (v: string) => v.trim() === '' || /^https?:\/\/\S+$/i.test(v.trim());

export function SettingsPage() {
  const toast = useToast();
  const { data, loading } = useAsync<Settings>(() => getRepo().getSettings(), []);
  const [form, setForm] = useState<Settings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data]);

  if (loading || !form) return <Spinner label="Loading settings…" />;

  // Bind the narrowed value for use inside event closures.
  const current: Settings = form;
  // Computed keys with a union type need an assertion — a known TS limitation.
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => setForm({ ...current, [key]: value } as Settings);
  const setSocial = (key: keyof Settings['social'], value: string) =>
    setForm({ ...current, social: { ...current.social, [key]: value } } as Settings);

  async function save(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (current.title.trim().length < 2) next.title = 'Title is too short.';
    for (const f of URL_FIELDS) if (!isHttp(current.social[f.key])) next[`social.${f.key}`] = 'Enter an http(s) URL or leave it blank.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaving(true);
    try {
      await getRepo().saveSettings(current);
      toast('Settings saved');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save settings', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="space-y-6" onSubmit={save} noValidate>
      <header>
        <h1 className="font-display text-2xl font-semibold">Site settings</h1>
        <p className="mt-1 text-sm text-muted">Public-facing details and operational toggles.</p>
      </header>

      <Card>
        <CardHeader title="Site" description="Shown in the header, footer and default metadata." />
        <CardBody className="space-y-4">
          <Field label="Title" error={errors.title}>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)} />
          </Field>
          <Field label="Tagline">
            <Input value={form.tagline} onChange={(e) => set('tagline', e.target.value)} />
          </Field>
          <Field label="Default description" hint="Used when a page has no meta description of its own (≤ 300 characters).">
            <Textarea rows={3} maxLength={300} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Social links" description="Empty links are omitted from the footer entirely — no dead icons." />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          {URL_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} error={errors[`social.${f.key}`]}>
              <Input
                type="url" inputMode="url" placeholder={f.placeholder}
                value={form.social[f.key]}
                onChange={(e) => setSocial(f.key, e.target.value)}
              />
            </Field>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Display" />
        <CardBody>
          <label className="flex items-start justify-between gap-6">
            <span>
              <span className="block text-sm font-medium">Hide view counts</span>
              <span className="mt-0.5 block text-xs text-muted">Hides the view counter on article cards, the trending rail and article pages.</span>
            </span>
            <Switch checked={form.hideViewCounts} onChange={(v) => set('hideViewCounts', v)} label="Hide view counts" />
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Content lifecycle" />
        <CardBody>
          <label className="flex items-start justify-between gap-6">
            <span>
              <span className="block text-sm font-medium">Pause article archival</span>
              <span className="mt-0.5 block text-xs text-muted">When enabled the daily job skips archiving articles older than 48 hours. Already-archived articles are unaffected.</span>
            </span>
            <Switch checked={form.pauseArchival} onChange={(v) => set('pauseArchival', v)} label="Pause article archival" />
          </label>
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-md text-xs text-muted">
          Saving is idempotent and writes an <code>audit_log</code> entry with the actor and timestamp.
          {isDemoMode ? ' Demo mode: changes last for this session only.' : ''}
        </p>
        <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
      </div>
    </form>
  );
}
